<script setup lang="ts">
import { computed, defineComponent, h, markRaw } from 'vue'

import clsx from 'clsx'

import IconAlignCenter from '@/components/atoms/icons/IconAlignCenter.vue'
import IconAlignLeft from '@/components/atoms/icons/IconAlignLeft.vue'
import IconAlignRight from '@/components/atoms/icons/IconAlignRight.vue'

type AlignmentValue = 'left' | 'center' | 'right' | 'justify'

const props = withDefaults(defineProps<{
    value?: string | null
    disabled?: boolean
    alignments?: AlignmentValue[]
}>(), {
    value: null,
    disabled: false,
    alignments: () => ['left', 'center', 'right'],
})

const emit = defineEmits<{
    change: [value: AlignmentValue]
}>()

const IconAlignJustify = defineComponent({
    name: 'BetaIconAlignJustifyFallback',
    setup() {
        return () => h(
            'svg',
            {
                xmlns: 'http://www.w3.org/2000/svg',
                width: '24',
                height: '24',
                viewBox: '0 0 24 24',
                fill: 'none',
                stroke: 'currentColor',
            },
            [
                h('path', {
                    d: 'M2.3999 7.79993H21.5999M2.3999 12H21.5999M2.3999 16.2H21.5999',
                    'stroke-width': '2',
                    'stroke-linecap': 'round',
                    'stroke-linejoin': 'round',
                }),
            ],
        )
    },
})

const iconMap = {
    left: markRaw(IconAlignLeft),
    center: markRaw(IconAlignCenter),
    right: markRaw(IconAlignRight),
    justify: markRaw(IconAlignJustify),
} satisfies Record<AlignmentValue, any>

const srLabelMap = {
    left: 'Alinhar à esquerda',
    center: 'Alinhar ao centro',
    right: 'Alinhar à direita',
    justify: 'Justificar',
} satisfies Record<AlignmentValue, string>

const options = computed(() => props.alignments.map((alignment) => ({
    value: alignment,
    icon: iconMap[alignment],
    srLabel: srLabelMap[alignment],
})))

const gridClass = computed(() => (
    options.value.length === 4 ? 'grid-cols-4' : 'grid-cols-3'
))

const onSelectAlignment = (alignment: AlignmentValue) => {
    if (props.disabled) return

    emit('change', alignment)
}
</script>

<template>
    <div :class="clsx('setting-align-options', gridClass)">
        <button
            v-for="option in options"
            :key="option.value"
            type="button"
            :data-align-option="option.value"
            :aria-pressed="props.value === option.value"
            :disabled="props.disabled"
            :class="clsx(
                'setting-align-options__button',
                props.value === option.value && 'is-active',
                props.disabled && 'is-disabled'
            )"
            @click="onSelectAlignment(option.value)"
        >
            <span class="sr-only">{{ option.srLabel }}</span>

            <component
                :is="option.icon"
                :class="clsx(
                    'setting-align-options__icon',
                    props.value === option.value ? 'opacity-100' : 'opacity-30'
                )"
            />
        </button>
    </div>
</template>

<style scoped>
.setting-align-options {
    @apply grid gap-2;
}

.setting-align-options__button {
    @apply flex h-11 items-center justify-center rounded-2xl border border-[#E6ECF5] bg-white transition-all duration-200 hover:border-[#C9D7EA] hover:bg-[#F7FAFE];
}

.setting-align-options__button.is-active {
    @apply border-[var(--main-color)] bg-[rgba(89,124,255,0.07)];
}

.setting-align-options__button.is-disabled {
    @apply cursor-not-allowed opacity-60 hover:border-[#E6ECF5] hover:bg-white;
}

.setting-align-options__icon {
    @apply stroke-[var(--main-color)] transition-opacity duration-200;
}
</style>
